/**
 * Reading — and only reading — the dude's inventory tree.
 *
 * `savedat.ts` keeps this span opaque and says why: the tree cannot be walked
 * without the item protos, so the codec anchors past it instead of parsing it.
 * That is still true of the *codec*. It stops being true once the install's own
 * protos are in hand, which is exactly what `gamedata/items.ts` provides — and
 * with them the walk is arithmetic, not inference.
 *
 * **This module never produces bytes.** It is a display decoder. The contract
 * in the design doc is unchanged and load-bearing: the codec does not write
 * this span, inventory mutations go through the engine bridge, and nothing here
 * gives the editor a second way to do it. A wrong walk here shows a wrong list;
 * a wrong walk in a writer corrupts a playthrough two hours later.
 *
 * ## The shape, from `_obj_save_obj` (engine/src/object.cc:3569)
 *
 * After the dude's own 32-word object header, one record per inventory slot:
 *
 *     quantity:i32, then a nested object, recursively
 *
 * A nested object is `objectWrite` (object.cc:704) — 18 int32s, `id` through
 * `scriptIndex` — followed by `objectDataWrite` (proto.cc:656), and then that
 * object's own inventory in the same shape. `objectDataWrite` always starts
 * with three int32s (`inventory.length`, `inventory.capacity`, and a literal 0
 * where a pointer used to live) and one more for `data.flags`, and then emits
 * a number of extra int32s that depends on the object's type:
 *
 * | object type from pid | extra int32s |
 * |---|---|
 * | critter (1) | 10 — 7 of combat data, then hp, radiation, poison |
 * | item (0), weapon | 2 — ammo quantity, ammo type pid |
 * | item (0), ammo / misc / key | 1 |
 * | item (0), armor / container | 0 |
 *
 * The item row is the one that needs the protos: `proto->item.type` is not in
 * the save. Scenery and exit-grid misc objects have their own rules and cannot
 * be in a critter's inventory, so meeting one means the walk has gone wrong and
 * we stop rather than resynchronise.
 *
 * ## Equipped items
 *
 * `ObjectFlags` (engine/src/obj_types.h:191): `OBJECT_IN_LEFT_HAND` 0x1000000,
 * `OBJECT_IN_RIGHT_HAND` 0x2000000, `OBJECT_WORN` 0x4000000. The flag is on the
 * item object, at word 9 of `objectWrite`.
 *
 * ## Confidence
 *
 * The walk is checked, not trusted: it must consume the span exactly, and it
 * must find exactly `dude.inventoryLength` top-level records. Coming up short
 * or long means the protos do not match the save, and the caller is told that
 * rather than shown a plausible-looking list.
 */

import type { ItemTypeName } from '../mods/fields';
import type { SaveDat } from './savedat';

/** `objectWrite` emits this many int32s before `objectDataWrite` starts. */
const OBJECT_HEADER_WORDS = 18;
/** `objectDataWrite`'s fixed prefix: inventory length, capacity, pointer, flags. */
const OBJECT_DATA_PREFIX_WORDS = 4;
/** A critter's `objectDataWrite` tail: 7 combat words, then hp/radiation/poison. */
const CRITTER_DATA_TAIL_WORDS = 10;

/** Word index of `flags` inside `objectWrite`. */
const FLAGS_WORD = 9;
/** Word index of `pid` inside `objectWrite`. */
const PID_WORD = 11;

const OBJECT_IN_LEFT_HAND = 0x1000000;
const OBJECT_IN_RIGHT_HAND = 0x2000000;
const OBJECT_WORN = 0x4000000;

/** Depth cap, matching the engine's own `LS_MAX_INV_DEPTH` hardening. */
const MAX_DEPTH = 16;

export type EquippedSlot = 'left hand' | 'right hand' | 'worn';

export interface InventoryEntry {
  /** The stack size the save records for this slot. */
  quantity: number;
  pid: number;
  /** Object flags word. `equipped` is derived from it. */
  flags: number;
  /** Which slot holds it, or null when it is loose in the pack. */
  equipped: EquippedSlot | null;
  /** Contents, for a container. Empty for everything else. */
  contents: InventoryEntry[];
}

export type InventoryRead =
  | { ok: true; entries: InventoryEntry[] }
  | { ok: false; reason: string };

/**
 * How many int32s `objectDataWrite` adds past its fixed prefix, for an item of
 * this type. `null` means "we do not know this item's proto", which is a
 * refusal rather than a guess — the wrong answer here desynchronises every
 * byte after it.
 */
function itemExtraWords(type: ItemTypeName): number {
  switch (type) {
    case 'weapon':
      return 2;
    case 'ammo':
    case 'misc':
    case 'key':
      return 1;
    default:
      return 0;
  }
}

export function equippedSlot(flags: number): EquippedSlot | null {
  if ((flags & OBJECT_IN_LEFT_HAND) !== 0) return 'left hand';
  if ((flags & OBJECT_IN_RIGHT_HAND) !== 0) return 'right hand';
  if ((flags & OBJECT_WORN) !== 0) return 'worn';
  return null;
}

/**
 * Walks the opaque span. `itemTypeOf` is the install's protos, keyed by pid;
 * returning null for a pid stops the walk, because an unknown item type means
 * an unknown record length.
 */
export function readInventory(
  save: SaveDat,
  itemTypeOf: (pid: number) => ItemTypeName | null
): InventoryRead {
  const bytes = save.inventoryBlob;
  if (bytes.length % 4 !== 0) {
    return { ok: false, reason: 'the inventory span is not a whole number of int32s' };
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const words = bytes.length / 4;
  let at = 0;

  const word = (): number => {
    const v = view.getInt32(at * 4, false);
    at += 1;
    return v;
  };

  const readObject = (depth: number): InventoryEntry | string => {
    if (depth > MAX_DEPTH) return 'the inventory nests deeper than the engine allows';
    if (at + OBJECT_HEADER_WORDS + OBJECT_DATA_PREFIX_WORDS > words) {
      return 'the inventory span ends inside an object header';
    }
    const start = at;
    at += OBJECT_HEADER_WORDS;
    const flags = view.getUint32(start * 4 + FLAGS_WORD * 4, false);
    const pid = view.getInt32(start * 4 + PID_WORD * 4, false);

    const childCount = word(); // inventory.length
    word(); // inventory.capacity
    word(); // the literal 0 where a pointer used to be
    word(); // data.flags

    const objectType = (pid >>> 24) & 0xff;
    if (objectType === 1) {
      // A critter — a party member's own object can appear here.
      if (at + CRITTER_DATA_TAIL_WORDS > words) return 'the inventory span ends inside a critter record';
      at += CRITTER_DATA_TAIL_WORDS;
    } else if (objectType === 0) {
      const type = itemTypeOf(pid);
      if (type === null) {
        return `item ${pid} is not in this install's protos, so its record length is unknown`;
      }
      const extra = itemExtraWords(type);
      if (at + extra > words) return 'the inventory span ends inside an item record';
      at += extra;
    } else {
      return `object type ${objectType} cannot be carried, so the walk has lost its place`;
    }

    if (childCount < 0 || childCount > words) return 'an inventory record claims an impossible number of contents';

    const contents: InventoryEntry[] = [];
    for (let i = 0; i < childCount; i++) {
      if (at >= words) return 'the inventory span ends inside a container';
      const quantity = word();
      const child = readObject(depth + 1);
      if (typeof child === 'string') return child;
      child.quantity = quantity;
      contents.push(child);
    }

    return { quantity: 1, pid, flags, equipped: equippedSlot(flags), contents };
  };

  const entries: InventoryEntry[] = [];
  const expected = save.dude.inventoryLength;
  if (expected < 0 || expected > words) {
    return { ok: false, reason: `the dude object claims ${expected} inventory slots` };
  }
  for (let i = 0; i < expected; i++) {
    if (at >= words) return { ok: false, reason: 'the inventory span ran out before its last slot' };
    const quantity = word();
    const entry = readObject(0);
    if (typeof entry === 'string') return { ok: false, reason: entry };
    entry.quantity = quantity;
    entries.push(entry);
  }

  // The span's own end is the check that the whole walk was right: every record
  // length fed the next offset, so consuming exactly the span means every one
  // of them was correct.
  if (at !== words) {
    return {
      ok: false,
      reason: `the walk finished ${((words - at) * 4).toString()} bytes short of the end of the span`,
    };
  }
  return { ok: true, entries };
}

/** One row per pid: how many are carried, and how many of those are equipped. */
export interface InventoryStack {
  pid: number;
  /** Total across every slot holding this pid, including equipped ones. */
  count: number;
  /** How many of `count` are in a hand or worn, and so cannot be removed. */
  equippedCount: number;
  /** Which slots those are, for the label. */
  slots: EquippedSlot[];
  /** True when this pid is only ever seen inside a container. */
  inContainerOnly: boolean;
}

/**
 * Flattens the tree into one row per pid.
 *
 * Containers are flattened in because `itemRemove` works on the dude's whole
 * inventory tree, so a shotgun in a bag is as removable as one in the pack —
 * but the row says where it is, because "I do not have that" is the first thing
 * a player thinks when a count does not match what the game showed them.
 */
export function stackInventory(entries: InventoryEntry[]): InventoryStack[] {
  const byPid = new Map<number, InventoryStack & { topLevel: number }>();
  const visit = (list: InventoryEntry[], nested: boolean) => {
    for (const entry of list) {
      let row = byPid.get(entry.pid);
      if (!row) {
        row = { pid: entry.pid, count: 0, equippedCount: 0, slots: [], inContainerOnly: true, topLevel: 0 };
        byPid.set(entry.pid, row);
      }
      const n = Math.max(1, entry.quantity);
      row.count += n;
      if (!nested) row.topLevel += n;
      if (entry.equipped) {
        row.equippedCount += n;
        if (!row.slots.includes(entry.equipped)) row.slots.push(entry.equipped);
      }
      if (entry.contents.length) visit(entry.contents, true);
    }
  };
  visit(entries, false);
  return [...byPid.values()].map(({ topLevel, ...row }) => ({ ...row, inContainerOnly: topLevel === 0 }));
}
